import { useState, useEffect, useCallback } from 'react'
import { adminService } from '../../services/adminService'
import {
  Users,
  Plus,
  Search,
  Edit2,
  Trash2,
  Link,
  Unlink,
  ToggleLeft,
  ToggleRight,
  ChevronLeft,
  ChevronRight,
  X,
  AlertTriangle,
  Loader2,
  RefreshCw,
} from 'lucide-react'

// ─── Types ─────────────────────────────────────────────────────────────────────

interface User {
  id: string
  name: string
  email: string
  role: string
  status: string
  createdAt: string
  businessId?: string | null
  businessName?: string | null
  emailVerified?: boolean
}

interface PaginatedResponse {
  items: User[]
  total: number
  page: number
  pageSize: number
  totalPages: number
}

interface Filters {
  role: string
  status: string
  search: string
  dateFrom: string
  dateTo: string
}

type ModalType =
  | 'create'
  | 'edit'
  | 'delete'
  | 'linkBusiness'
  | 'unlinkBusiness'
  | null

// ─── Constants ─────────────────────────────────────────────────────────────────

const ROLES = [
  { value: '', label: 'All Roles' },
  { value: 'business_owner', label: 'Business Owner' },
  { value: 'lender', label: 'Lender' },
  { value: 'admin', label: 'Admin' },
  { value: 'analyst', label: 'Analyst' },
  { value: 'viewer', label: 'Viewer' },
  { value: 'system', label: 'System' },
]

const STATUSES = [
  { value: '', label: 'All Statuses' },
  { value: 'active', label: 'Active' },
  { value: 'disabled', label: 'Disabled' },
  { value: 'pending', label: 'Pending Verification' },
]

const PAGE_SIZES = [10, 25, 50, 100]

// ─── Component ─────────────────────────────────────────────────────────────────

export function AdminUsers() {
  // Data state
  const [users, setUsers] = useState<User[]>([])
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Pagination
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  // Filters
  const [filters, setFilters] = useState<Filters>({
    role: '',
    status: '',
    search: '',
    dateFrom: '',
    dateTo: '',
  })

  // Modal state
  const [activeModal, setActiveModal] = useState<ModalType>(null)
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [modalLoading, setModalLoading] = useState(false)
  const [modalError, setModalError] = useState<string | null>(null)

  // Form state for create/edit
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'business_owner',
    businessId: '',
  })

  // Link business state
  const [linkBusinessId, setLinkBusinessId] = useState('')

  // ─── Data Fetching ─────────────────────────────────────────────────────────

  const fetchUsers = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params: Record<string, any> = {
        page,
        pageSize,
      }
      if (filters.role) params.role = filters.role
      if (filters.status) params.status = filters.status
      if (filters.search) params.search = filters.search
      if (filters.dateFrom) params.registeredAfter = filters.dateFrom
      if (filters.dateTo) params.registeredBefore = filters.dateTo

      const response = await adminService.listUsers(params)
      const data = response.data as PaginatedResponse
      setUsers(data.items || [])
      setTotal(data.total || 0)
      setTotalPages(data.totalPages || 0)
    } catch (err: any) {
      setError(err.message || 'Failed to load users')
      setUsers([])
    } finally {
      setLoading(false)
    }
  }, [page, pageSize, filters])

  useEffect(() => {
    fetchUsers()
  }, [fetchUsers])

  // ─── Filter Handlers ───────────────────────────────────────────────────────

  const handleFilterChange = (key: keyof Filters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }))
    setPage(1)
  }

  const clearFilters = () => {
    setFilters({ role: '', status: '', search: '', dateFrom: '', dateTo: '' })
    setPage(1)
  }

  const hasActiveFilters =
    filters.role || filters.status || filters.search || filters.dateFrom || filters.dateTo

  // ─── Modal Handlers ────────────────────────────────────────────────────────

  const openCreateModal = () => {
    setFormData({ name: '', email: '', role: 'business_owner', businessId: '' })
    setModalError(null)
    setActiveModal('create')
  }

  const openEditModal = (user: User) => {
    setSelectedUser(user)
    setFormData({
      name: user.name,
      email: user.email,
      role: user.role,
      businessId: user.businessId || '',
    })
    setModalError(null)
    setActiveModal('edit')
  }

  const openDeleteModal = (user: User) => {
    setSelectedUser(user)
    setModalError(null)
    setActiveModal('delete')
  }

  const openLinkBusinessModal = (user: User) => {
    setSelectedUser(user)
    setLinkBusinessId('')
    setModalError(null)
    setActiveModal('linkBusiness')
  }

  const openUnlinkBusinessModal = (user: User) => {
    setSelectedUser(user)
    setModalError(null)
    setActiveModal('unlinkBusiness')
  }

  const closeModal = () => {
    setActiveModal(null)
    setSelectedUser(null)
    setModalError(null)
  }

  // ─── CRUD Operations ───────────────────────────────────────────────────────

  const handleCreateUser = async () => {
    if (!formData.name.trim() || !formData.email.trim()) {
      setModalError('Name and email are required')
      return
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setModalError('Please enter a valid email address')
      return
    }
    setModalLoading(true)
    setModalError(null)
    try {
      const payload: Record<string, any> = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        role: formData.role,
      }
      if (formData.businessId.trim()) {
        payload.businessId = formData.businessId.trim()
      }
      await adminService.createUser(payload)
      closeModal()
      fetchUsers()
    } catch (err: any) {
      setModalError(err.message || 'Failed to create user')
    } finally {
      setModalLoading(false)
    }
  }

  const handleUpdateUser = async () => {
    if (!selectedUser) return
    if (!formData.name.trim() || !formData.email.trim()) {
      setModalError('Name and email are required')
      return
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setModalError('Please enter a valid email address')
      return
    }
    setModalLoading(true)
    setModalError(null)
    try {
      await adminService.updateUser(selectedUser.id, {
        name: formData.name.trim(),
        email: formData.email.trim(),
        role: formData.role,
      })
      closeModal()
      fetchUsers()
    } catch (err: any) {
      setModalError(err.message || 'Failed to update user')
    } finally {
      setModalLoading(false)
    }
  }

  const handleDeleteUser = async () => {
    if (!selectedUser) return
    setModalLoading(true)
    setModalError(null)
    try {
      await adminService.deleteUser(selectedUser.id)
      closeModal()
      fetchUsers()
    } catch (err: any) {
      setModalError(err.message || 'Failed to delete user')
    } finally {
      setModalLoading(false)
    }
  }

  const handleToggleStatus = async (user: User) => {
    try {
      if (user.status === 'active') {
        await adminService.disableUser(user.id)
      } else {
        await adminService.enableUser(user.id)
      }
    } catch (err: any) {
      // Ignore "already active" / "already disabled" errors (422) since
      // this can happen when the local state is stale
      const status = err?.response?.status || err?.statusCode
      if (status !== 422) {
        setError(err.message || 'Failed to toggle user status')
      }
    }
    // Brief delay to allow DB write to propagate, then refresh
    await new Promise(resolve => setTimeout(resolve, 1000))
    fetchUsers()
  }

  const handleLinkBusiness = async () => {
    if (!selectedUser || !linkBusinessId.trim()) {
      setModalError('Business ID is required')
      return
    }
    setModalLoading(true)
    setModalError(null)
    try {
      await adminService.linkBusiness(selectedUser.id, linkBusinessId.trim())
      closeModal()
      fetchUsers()
    } catch (err: any) {
      setModalError(err.message || 'Failed to link business')
    } finally {
      setModalLoading(false)
    }
  }

  const handleUnlinkBusiness = async () => {
    if (!selectedUser) return
    setModalLoading(true)
    setModalError(null)
    try {
      await adminService.unlinkBusiness(selectedUser.id)
      closeModal()
      fetchUsers()
    } catch (err: any) {
      setModalError(err.message || 'Failed to unlink business')
    } finally {
      setModalLoading(false)
    }
  }

  // ─── Pagination Helpers ────────────────────────────────────────────────────

  const getPageNumbers = (): (number | '...')[] => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1)
    }
    const pages: (number | '...')[] = [1]
    if (page > 3) pages.push('...')
    const start = Math.max(2, page - 1)
    const end = Math.min(totalPages - 1, page + 1)
    for (let i = start; i <= end; i++) pages.push(i)
    if (page < totalPages - 2) pages.push('...')
    pages.push(totalPages)
    return pages
  }

  // ─── Render Helpers ────────────────────────────────────────────────────────

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '—'
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    } catch {
      return '—'
    }
  }

  const getRoleBadge = (role: string) => {
    const colorMap: Record<string, string> = {
      admin: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
      business_owner: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
      lender: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
      analyst: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
      viewer: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
      system: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
    }
    return colorMap[role] || colorMap.viewer
  }

  const getStatusBadge = (status: string) => {
    const colorMap: Record<string, string> = {
      active: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
      disabled: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
      pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
    }
    return colorMap[status] || colorMap.pending
  }

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Users className="h-7 w-7 text-blue-600 dark:text-blue-400" />
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
              User Management
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Manage platform users, roles, and business links
            </p>
          </div>
        </div>
        <button
          onClick={openCreateModal}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
        >
          <Plus className="h-4 w-4" />
          Create User
        </button>
      </div>

      {/* Filters */}
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {/* Search */}
          <div className="relative lg:col-span-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Role Filter */}
          <select
            value={filters.role}
            onChange={(e) => handleFilterChange('role', e.target.value)}
            className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {ROLES.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>

          {/* Status Filter */}
          <select
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {STATUSES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>

          {/* Clear Filters */}
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="inline-flex items-center justify-center gap-1 px-3 py-2 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <X className="h-3.5 w-3.5" />
              Clear
            </button>
          )}
        </div>

        {/* Date Range */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mt-3">
          <span className="text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
            Registered:
          </span>
          <input
            type="date"
            value={filters.dateFrom}
            onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
            className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <span className="text-sm text-gray-400">to</span>
          <input
            type="date"
            value={filters.dateTo}
            onChange={(e) => handleFilterChange('dateTo', e.target.value)}
            className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="flex items-center gap-3 p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
          <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0" />
          <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          <button
            onClick={fetchUsers}
            className="ml-auto inline-flex items-center gap-1 text-sm text-red-700 dark:text-red-300 hover:underline"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Retry
          </button>
        </div>
      )}

      {/* Users Table */}
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600 dark:text-blue-400" />
            <span className="ml-3 text-sm text-gray-500 dark:text-gray-400">
              Loading users...
            </span>
          </div>
        ) : users.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-4">
            <Users className="h-12 w-12 text-gray-300 dark:text-gray-600 mb-3" />
            <p className="text-gray-500 dark:text-gray-400 text-sm">
              {hasActiveFilters
                ? 'No users match the current filters'
                : 'No users found'}
            </p>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="mt-2 text-sm text-blue-600 dark:text-blue-400 hover:underline"
              >
                Clear filters
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
                    <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-300">
                      Name
                    </th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-300">
                      Email
                    </th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-300">
                      Role
                    </th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-300">
                      Status
                    </th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-300">
                      Registered
                    </th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-300">
                      Business
                    </th>
                    <th className="text-right px-4 py-3 font-medium text-gray-600 dark:text-gray-300">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {users.map((user) => (
                    <tr
                      key={user.id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
                    >
                      <td className="px-4 py-3 font-medium text-gray-900 dark:text-white whitespace-nowrap">
                        {user.name}
                      </td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-300 whitespace-nowrap">
                        {user.email}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getRoleBadge(user.role)}`}
                        >
                          {user.role.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getStatusBadge(user.status)}`}
                        >
                          {user.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-300 whitespace-nowrap">
                        {formatDate(user.createdAt)}
                      </td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-300 whitespace-nowrap">
                        {user.businessName || user.businessId || '—'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => openEditModal(user)}
                            title="Edit user"
                            className="p-1.5 rounded-md text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:text-gray-400 dark:hover:text-blue-400 dark:hover:bg-blue-900/20 transition-colors"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleToggleStatus(user)}
                            title={user.status === 'active' ? 'Disable user' : 'Enable user'}
                            className="p-1.5 rounded-md text-gray-500 hover:text-yellow-600 hover:bg-yellow-50 dark:text-gray-400 dark:hover:text-yellow-400 dark:hover:bg-yellow-900/20 transition-colors"
                          >
                            {user.status === 'active' ? (
                              <ToggleRight className="h-4 w-4" />
                            ) : (
                              <ToggleLeft className="h-4 w-4" />
                            )}
                          </button>
                          {user.businessId ? (
                            <button
                              onClick={() => openUnlinkBusinessModal(user)}
                              title="Unlink business"
                              className="p-1.5 rounded-md text-gray-500 hover:text-orange-600 hover:bg-orange-50 dark:text-gray-400 dark:hover:text-orange-400 dark:hover:bg-orange-900/20 transition-colors"
                            >
                              <Unlink className="h-4 w-4" />
                            </button>
                          ) : (
                            <button
                              onClick={() => openLinkBusinessModal(user)}
                              title="Link business"
                              className="p-1.5 rounded-md text-gray-500 hover:text-green-600 hover:bg-green-50 dark:text-gray-400 dark:hover:text-green-400 dark:hover:bg-green-900/20 transition-colors"
                            >
                              <Link className="h-4 w-4" />
                            </button>
                          )}
                          <button
                            onClick={() => openDeleteModal(user)}
                            title="Delete user"
                            className="p-1.5 rounded-md text-gray-500 hover:text-red-600 hover:bg-red-50 dark:text-gray-400 dark:hover:text-red-400 dark:hover:bg-red-900/20 transition-colors"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                <span>
                  Showing {(page - 1) * pageSize + 1}–
                  {Math.min(page * pageSize, total)} of {total}
                </span>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value))
                    setPage(1)
                  }}
                  className="ml-2 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  {PAGE_SIZES.map((size) => (
                    <option key={size} value={size}>
                      {size} / page
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="p-1.5 rounded-md border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                {getPageNumbers().map((p, i) =>
                  p === '...' ? (
                    <span
                      key={`ellipsis-${i}`}
                      className="px-2 text-gray-400 dark:text-gray-500"
                    >
                      ...
                    </span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => setPage(p)}
                      className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                        p === page
                          ? 'bg-blue-600 text-white'
                          : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                    >
                      {p}
                    </button>
                  )
                )}
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="p-1.5 rounded-md border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* ─── Modals ──────────────────────────────────────────────────────────── */}

      {/* Create User Modal */}
      {activeModal === 'create' && (
        <ModalOverlay onClose={closeModal}>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Create User
              </h2>
            </div>
            <div className="p-6 space-y-4">
              {modalError && (
                <p className="text-sm text-red-600 dark:text-red-400">{modalError}</p>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData((f) => ({ ...f, name: e.target.value }))
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Full name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData((f) => ({ ...f, email: e.target.value }))
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="user@example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Role <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.role}
                  onChange={(e) =>
                    setFormData((f) => ({ ...f, role: e.target.value }))
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {ROLES.filter((r) => r.value).map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Business ID (optional)
                </label>
                <input
                  type="text"
                  value={formData.businessId}
                  onChange={(e) =>
                    setFormData((f) => ({ ...f, businessId: e.target.value }))
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Link to a business"
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
              <button
                onClick={closeModal}
                disabled={modalLoading}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateUser}
                disabled={modalLoading}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-60 transition-colors"
              >
                {modalLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                Create
              </button>
            </div>
          </div>
        </ModalOverlay>
      )}

      {/* Edit User Modal */}
      {activeModal === 'edit' && selectedUser && (
        <ModalOverlay onClose={closeModal}>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Edit User
              </h2>
            </div>
            <div className="p-6 space-y-4">
              {modalError && (
                <p className="text-sm text-red-600 dark:text-red-400">{modalError}</p>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData((f) => ({ ...f, name: e.target.value }))
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData((f) => ({ ...f, email: e.target.value }))
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Role
                </label>
                <select
                  value={formData.role}
                  onChange={(e) =>
                    setFormData((f) => ({ ...f, role: e.target.value }))
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {ROLES.filter((r) => r.value).map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
              <button
                onClick={closeModal}
                disabled={modalLoading}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateUser}
                disabled={modalLoading}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-60 transition-colors"
              >
                {modalLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                Save Changes
              </button>
            </div>
          </div>
        </ModalOverlay>
      )}

      {/* Delete Confirmation Modal */}
      {activeModal === 'delete' && selectedUser && (
        <ModalOverlay onClose={closeModal}>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-sm w-full mx-4">
            <div className="p-6 text-center">
              <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Delete User
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Are you sure you want to delete{' '}
                <span className="font-medium text-gray-900 dark:text-white">
                  {selectedUser.name}
                </span>
                ? This action will soft-delete the account and revoke all active sessions.
              </p>
              {modalError && (
                <p className="mt-3 text-sm text-red-600 dark:text-red-400">
                  {modalError}
                </p>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
              <button
                onClick={closeModal}
                disabled={modalLoading}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteUser}
                disabled={modalLoading}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-red-600 hover:bg-red-700 text-white rounded-lg disabled:opacity-60 transition-colors"
              >
                {modalLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                Delete
              </button>
            </div>
          </div>
        </ModalOverlay>
      )}

      {/* Link Business Modal */}
      {activeModal === 'linkBusiness' && selectedUser && (
        <ModalOverlay onClose={closeModal}>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-sm w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Link Business
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Link a business to{' '}
                <span className="font-medium">{selectedUser.name}</span>
              </p>
            </div>
            <div className="p-6 space-y-4">
              {modalError && (
                <p className="text-sm text-red-600 dark:text-red-400">{modalError}</p>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Business ID <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={linkBusinessId}
                  onChange={(e) => setLinkBusinessId(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter business ID"
                />
              </div>
              <div className="p-3 rounded-lg bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800">
                <p className="text-xs text-yellow-700 dark:text-yellow-300">
                  If this business is already linked to another user, it will be
                  reassigned to this user.
                </p>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
              <button
                onClick={closeModal}
                disabled={modalLoading}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleLinkBusiness}
                disabled={modalLoading}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-green-600 hover:bg-green-700 text-white rounded-lg disabled:opacity-60 transition-colors"
              >
                {modalLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                Link
              </button>
            </div>
          </div>
        </ModalOverlay>
      )}

      {/* Unlink Business Confirmation Modal */}
      {activeModal === 'unlinkBusiness' && selectedUser && (
        <ModalOverlay onClose={closeModal}>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-sm w-full mx-4">
            <div className="p-6 text-center">
              <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                <Unlink className="h-6 w-6 text-orange-600 dark:text-orange-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Unlink Business
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Are you sure you want to unlink the business from{' '}
                <span className="font-medium text-gray-900 dark:text-white">
                  {selectedUser.name}
                </span>
                ? The user will no longer be associated with{' '}
                <span className="font-medium text-gray-900 dark:text-white">
                  {selectedUser.businessName || selectedUser.businessId}
                </span>
                .
              </p>
              {modalError && (
                <p className="mt-3 text-sm text-red-600 dark:text-red-400">
                  {modalError}
                </p>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
              <button
                onClick={closeModal}
                disabled={modalLoading}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleUnlinkBusiness}
                disabled={modalLoading}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-orange-600 hover:bg-orange-700 text-white rounded-lg disabled:opacity-60 transition-colors"
              >
                {modalLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                Unlink
              </button>
            </div>
          </div>
        </ModalOverlay>
      )}
    </div>
  )
}

// ─── Modal Overlay Component ─────────────────────────────────────────────────

function ModalOverlay({
  children,
  onClose,
}: {
  children: React.ReactNode
  onClose: () => void
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative z-10">{children}</div>
    </div>
  )
}
